/**
 * @besper/fe-shared – Toast
 * Single toast notification; styling via theme CSS variables from deployment/config/env/.platform_fe_*_config.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CheckCircle, XCircle, AlertCircle, Info, X } from 'lucide-react';
import './Toast.css';

const Toast = ({
  id,
  type = 'info',
  title,
  message,
  duration = 5000,
  onClose,
  show = true,
  position = 'top-right',
  variant = 'dark',
  style,
}) => {
  const [isVisible, setIsVisible] = useState(show);
  const [isExiting, setIsExiting] = useState(false);
  const [progress, setProgress] = useState(100);
  const progressIntervalRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  const handleClose = useCallback(() => {
    setIsExiting(true);
    if (progressIntervalRef.current) {
      cancelAnimationFrame(progressIntervalRef.current);
    }
    setTimeout(() => {
      setIsVisible(false);
      if (onClose) {
        onClose(id);
      }
    }, 300);
  }, [id, onClose]);

  useEffect(() => {
    if (duration > 0 && isVisible && !isExiting) {
      startTimeRef.current = Date.now();
      setProgress(100);

      const updateProgress = () => {
        const elapsed = Date.now() - startTimeRef.current;
        const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
        setProgress(remaining);

        if (remaining > 0) {
          progressIntervalRef.current = requestAnimationFrame(updateProgress);
        } else {
          setProgress(0);
          if (progressIntervalRef.current) {
            cancelAnimationFrame(progressIntervalRef.current);
          }
          handleClose();
        }
      };

      progressIntervalRef.current = requestAnimationFrame(updateProgress);

      const timer = setTimeout(() => {
        if (progressIntervalRef.current) {
          cancelAnimationFrame(progressIntervalRef.current);
        }
        handleClose();
      }, duration);

      return () => {
        clearTimeout(timer);
        if (progressIntervalRef.current) {
          cancelAnimationFrame(progressIntervalRef.current);
        }
      };
    }
  }, [duration, isVisible, isExiting, handleClose]);

  if (!isVisible) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle className="toast-icon toast-icon-success" />;
      case 'error':
        return <XCircle className="toast-icon toast-icon-error" />;
      case 'warning':
        return <AlertCircle className="toast-icon toast-icon-warning" />;
      case 'info':
      default:
        return <Info className="toast-icon toast-icon-info" />;
    }
  };

  return (
    <div
      className={`toast toast-${type} toast-${position} toast-variant-${variant} ${isExiting ? 'toast-exiting' : ''}`}
      role="alert"
      aria-live="polite"
      style={style}
    >
      <div className="toast-content">
        <div className="toast-icon-container">
          {getIcon()}
        </div>
        <div className="toast-text">
          {title && <div className="toast-title">{title}</div>}
          {message && <div className="toast-message">{message}</div>}
        </div>
        <button
          className="toast-close"
          onClick={handleClose}
          aria-label="Close notification"
        >
          <X size={16} />
        </button>
      </div>
      {variant === 'dark' && duration > 0 && (
        <div className="toast-progress-bar">
          <div
            className="toast-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

export default Toast;
